import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CalendarClock, BookOpen, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import CivicLawBanner from "../components/CivicLawBanner";

/**
 * Guidance — what to do, why, and by when.
 *
 * Deliberately *not* a chat box. Every item is a dated, sourced piece of scaffolding: the
 * thing to do, the reason, the timing, and the section of the domain reference it comes
 * from. Nothing here says "you should" and nothing predicts an outcome — asserting a legal
 * position is out of bounds (blocking rule 1.3), and anything not in the domain reference
 * isn't asserted at all (1.4).
 *
 * Deadlines are computed by date arithmetic on the estate's own recorded dates, so this
 * page costs nothing to render.
 */

const CATEGORY_ORDER = ["charges", "records", "agent", "company"];

function DeadlinePill({ d }) {
    const tone = d.is_overdue
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : d.days_remaining <= 30
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
    return (
        <div className={`rounded-md border px-3 py-2.5 ${tone}`} data-testid={`deadline-${d.key}`}>
            <div className="text-sm font-medium leading-snug">{d.title}</div>
            <div className="text-xs mt-1 tabular-nums">
                {d.due_date}
                {" · "}
                {d.is_overdue
                    ? `${Math.abs(d.days_remaining)} days ago`
                    : `in ${d.days_remaining} days`}
            </div>
            {d.note && <div className="text-xs mt-1.5 opacity-80">{d.note}</div>}
        </div>
    );
}

export default function Guidance() {
    const [data, setData] = useState(null);
    const [deadlines, setDeadlines] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [g, d] = await Promise.all([
                    api.get("/guidance"),
                    api.get("/guidance/deadlines"),
                ]);
                setData(g.data);
                setDeadlines(d.data);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not load guidance");
            }
        })();
    }, []);

    if (!data) return <div className="p-8 text-sm text-slate-500" data-testid="guidance-loading">Loading…</div>;

    const byCategory = (key) => data.items.filter((i) => i.category === key);
    const label = (key) => data.categories.find((c) => c.key === key)?.label || key;

    return (
        <div className="max-w-3xl mx-auto px-6 py-10" data-testid="guidance-page">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <BookOpen size={14} /> Evidence and timing
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">
                What to do, and by when
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                {data.scope_note}
            </p>
            <CivicLawBanner />

            {deadlines?.deadlines?.length === 0 && (
                <section className="mt-8" data-testid="guidance-no-deadlines">
                    <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                        <CalendarClock size={17} className="opacity-70" /> Coming up
                    </h2>
                    <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-5">
                        <p className="text-sm text-slate-600 m-0">
                            No dated deadlines yet. Add your company's confirmation statement review
                            date and accounting reference date under{" "}
                            <Link to="/onboarding" className="text-slate-900 font-medium underline">
                                Set up estate
                            </Link>{" "}
                            and they'll appear here.
                        </p>
                        <p className="text-xs text-slate-500 mt-2 m-0">
                            We only show deadlines we can actually calculate from your own dates —
                            nothing is estimated.
                        </p>
                    </div>
                </section>
            )}

            {deadlines?.deadlines?.length > 0 && (
                <section className="mt-8">
                    <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                        <CalendarClock size={17} className="opacity-70" /> Coming up
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-2.5 mt-3">
                        {deadlines.deadlines.map((d) => (
                            <DeadlinePill key={d.key} d={d} />
                        ))}
                    </div>
                    {deadlines.note && (
                        <p className="text-xs text-slate-500 mt-2.5">{deadlines.note}</p>
                    )}
                </section>
            )}

            {CATEGORY_ORDER.map((cat) => {
                const items = byCategory(cat);
                if (!items.length) return null;
                return (
                    <section key={cat} className="mt-9">
                        <h2 className="font-heading font-semibold text-lg">{label(cat)}</h2>
                        <div className="mt-3 space-y-3">
                            {items.map((item) => (
                                <div
                                    key={item.key}
                                    className="rounded-lg border border-slate-200 bg-white p-4"
                                    data-testid={`guidance-${item.key}`}
                                >
                                    <h3 className="font-medium text-slate-900">{item.title}</h3>
                                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{item.why}</p>
                                    <div className="mt-3 grid sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                                        <span className="text-slate-400 uppercase tracking-wide">When</span>
                                        <span className="text-slate-700">{item.when}</span>
                                        <span className="text-slate-400 uppercase tracking-wide">Do</span>
                                        <span className="text-slate-700">{item.action}</span>
                                    </div>
                                    {item.payment_framing && (
                                        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex gap-2">
                                            <AlertCircle size={14} className="text-amber-700 shrink-0 mt-0.5" />
                                            <p className="text-xs text-amber-900 leading-relaxed">
                                                {item.payment_framing}
                                            </p>
                                        </div>
                                    )}
                                    <p className="text-[11px] text-slate-400 mt-3">
                                        Source: {item.source}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}

            <p className="mt-10 text-xs text-slate-500 leading-relaxed max-w-2xl border-t border-slate-200 pt-5">
                This sets out evidence and timing only — it is not legal advice, and nothing here says a
                charge is or isn't payable. Review anything important with your own adviser before acting.
            </p>
        </div>
    );
}
